/**
 * Générateurs des ITEMS NATIONAUX : français, 6e, Lexique, campagne 2026.
 *
 * Même contrat que les modules de maths (2026/6e/automatismes.js, le modèle) : un générateur par item,
 * précédé de sa fiche (lue sur la CAPTURE de l'item, jamais sur le champ texte de la base, qui coupe les
 * phrases de Q09 et note entre crochets les mots soulignés de Q11), ITEMS avec la bonne réponse officielle
 * exacte, CATEGORIES, generate.
 *
 * Une variante ne calcule rien : elle tire dans des BANQUES de mots et de phrases écrites à la main et
 * vérifiées une à une (un préfixe est un vrai préfixe dans ce mot, une famille une vraie famille, un
 * synonyme l'est dans la phrase donnée, une expression a bien ce sens). Chaque banque garde l'attendu de
 * l'item, sa procédure, son niveau de vocabulaire et ses pièges : chaque mauvaise réponse officielle a une
 * raison, et la variante en fabrique une de même nature avec d'autres mots. L'item officiel est toujours
 * l'une des variantes. Les gardes vérifient ce qui se vérifie (un mot formé = le préfixe + un mot qui
 * existe seul, la colonne d'une phrase de Q11 = sa construction, les mots soulignés de Q14 = les lignes
 * du tableau, quatre choix distincts pour le correcteur) et lèvent une erreur au lieu de laisser partir
 * une variante fausse.
 * Orthographe : celle des items, rectifiée (« abimée », « maitre ») ; ailleurs, les mots que la réforme
 * de 1990 touche sont évités. Une personne nommée n'est jamais reprise par « il » ou « elle » (Q08).
 */
import { randomChoice, shuffle, makeQcm, makeVraiFaux } from '../../../../utils';
import { normalizeAnswer } from '../../../../../utils/questionUtils';

const opts = (label) => ({ niveau: '6eme', matiere: 'francais', attendu: null, label });

/** Second chemin (REGLES_EQUIPE § 3) : une variante mal construite lève une erreur au lieu de partir. */
const verifier = (condition, message) => {
    if (!condition) throw new Error(`Item national de français 6e, lexique, ${message}`);
};

/** Le texte d'un mot ou d'une phrase tel que le correcteur le compare : minuscules, sans accent. */
const nu = (s) => normalizeAnswer(String(s));

/** n éléments distincts d'une banque, au hasard. */
const tirer = (banque, n) => shuffle(banque).slice(0, n);

/** Un guillemet ne se sépare jamais de son mot en fin de ligne (espaces insécables). */
const INSECABLE = String.fromCharCode(160);
const insecables = (html) => html.replace(/« /g, `«${INSECABLE}`).replace(/ »/g, `${INSECABLE}»`);

/** Le QCM d'une variante : quatre choix en texte, le juste en premier, distincts pour le correcteur. */
const qcm = (juste, faux, texte, label) => {
    const choix = [juste, ...faux];
    verifier(choix.length === 4 && choix.every((c) => typeof c === 'string' && c.trim() !== ''),
        `quatre choix en texte : ${choix.join(' | ')}`);
    verifier(new Set(choix.map(nu)).size === 4, `choix confondus : ${choix.join(' | ')}`);
    return makeQcm(choix, juste, insecables(texte), opts(label));
};

/** Le tableau à cocher d'une variante : lignes distinctes, sans crochet ni barre (le format [ligne|colonne]). */
const tableau = (lignes, bonnes, colonnes, texte, label) => {
    verifier(lignes.length === 4 && new Set(lignes.map(nu)).size === 4, `quatre lignes distinctes : ${lignes.join(' | ')}`);
    verifier(lignes.every((l) => !/[[\]|]/.test(l)), `crochet ou barre dans une ligne : ${lignes.join(' | ')}`);
    verifier(bonnes.length === 4 && bonnes.every((b) => Number.isInteger(b) && b >= 0 && b < colonnes.length),
        `colonnes justes : ${bonnes.join(', ')}`);
    return makeVraiFaux(lignes, bonnes, insecables(texte), { ...opts(label), colonnes });
};

/**
 * Q01 : Identifier un radical transparent.
 * Item : « Les mots suivants sont formés avec le préfixe mal- qui signifie « qui n'est pas », sauf un.
 *   Trouver l'intrus. » ; malade (juste), malheureux, malhabile, malchanceux.
 * Domaine : un préfixe qui signifie « qui n'est pas », trois mots formés avec lui (le préfixe devant un
 *   mot qui existe seul : mal + heureux) et un intrus qui commence par les mêmes lettres sans être formé
 *   avec lui (ce qui suit n'est pas un mot : mal + ade). Quatre préfixes : mal- (7 mots formés, 3 intrus :
 *   malade, malin, malicieux), in- (12 et 5 : intelligent, intéressant, ingénieux, intense, inventif),
 *   im- (8 et 1 : impressionnant), dés- (4 et 3 : désolé, désert, désireux) ; 1 273 variantes.
 *   Écartés : malpoli (familier), malveillant, malodorant, malintentionné (mal- y dit « mal », pas « qui
 *   n'est pas »), maltraiter, malaise ; important (ce qui suit, « portant », est un mot : piège d'une autre
 *   difficulté que malade) ; innocent, inquiet, insolent, infirme, immense, impeccable (négatifs en latin,
 *   donc discutables comme intrus) ; désastreux.
 * Erreurs : malheureux, malhabile, malchanceux sont bien formés avec mal- : l'élève qui en choisit un n'a
 *   pas cherché le mot de base (heureux, habile, chanceux), souvent faute de connaître la base (habile), et
 *   garde malade, qui commence par mal-, pour un mot formé. Les variantes posent trois mots formés, dont des
 *   bases moins courantes (adroit, exact, pair, organisé), et un intrus de même nature que malade.
 */
const L01 = 'Identifier un radical transparent';
/** Un préfixe qui signifie « qui n'est pas » : les mots de base qu'il forme, et des intrus qui commencent pareil. */
const PREFIXES_Q01 = [
    {
        prefixe: 'mal',
        bases: ['heureux', 'habile', 'chanceux', 'honnête', 'propre', 'adroit', 'sain'],
        intrus: ['malade', 'malin', 'malicieux'],
    },
    {
        prefixe: 'in',
        bases: ['utile', 'visible', 'capable', 'connu', 'juste', 'correct', 'exact', 'complet', 'égal', 'attentif',
            'certain', 'fidèle'],
        intrus: ['intelligent', 'intéressant', 'ingénieux', 'intense', 'inventif'],
    },
    {
        prefixe: 'im',
        bases: ['poli', 'possible', 'patient', 'prudent', 'pur', 'mobile', 'prévu', 'pair'],
        intrus: ['impressionnant'],
    },
    {
        prefixe: 'dés',
        bases: ['agréable', 'obéissant', 'ordonné', 'organisé'],
        intrus: ['désolé', 'désert', 'désireux'],
    },
];
const genQ01 = () => {
    const { prefixe, bases, intrus } = randomChoice(PREFIXES_Q01);
    const formes = tirer(bases, 3).map((base) => `${prefixe}${base}`);
    const juste = randomChoice(intrus);
    verifier(juste.startsWith(prefixe) && !bases.includes(juste.slice(prefixe.length)),
        `Q01 : l'intrus ${juste} ne doit pas être formé avec ${prefixe}-`);
    return qcm(juste, formes,
        `<p>Les mots suivants sont formés avec le préfixe ${prefixe}- qui signifie « qui n'est pas », sauf un.</p>`
        + '<p>Trouver l\'intrus.</p>',
        L01);
};

/**
 * Q02 : Déduire le sens d'un suffixe en contexte.
 * Item : « « Cette pomme est très abimée mais reste mangeable. » L'utilisation du suffixe « -able »
 *   signifie que la pomme… » ; ne doit pas être mangée., peut être mangée. (juste), est mangée avec
 *   plaisir., est mangée à moitié.
 * Domaine : une phrase « X est [un défaut] mais reste [verbe]-able (ou -ible) » ; la réponse reste
 *   « peut être [participe] », accordé avec X. 19 phrases (mangeable, lisible, buvable, réparable,
 *   utilisable, habitable, transportable, cultivable, respirable, visible, pardonnable, réalisable,
 *   supportable), 19 variantes. Écartés : lavable, jetable, portable (le sens « peut être… » y
 *   est figé ou ambigu), navigable (« être navigué » ne se dit pas), mettable (familier et rare pour un
 *   élève de 6e : TRANCHÉ LE 11/10, même complexité que l'item).
 * Erreurs : « ne doit pas être mangée » : le défaut du début (« très abimée ») pris pour la conclusion, le
 *   « mais » oublié ; « est mangée avec plaisir » : le suffixe lu comme une appréciation, comme dans
 *   « agréable » ; « est mangée à moitié » : l'état de la pomme pris pour une action à moitié faite. Les
 *   variantes recalculent les trois sur leur participe ; le défaut précède toujours le « mais ».
 */
const L02 = 'Déduire le sens d\'un suffixe en contexte';
/** [phrase, suffixe, le groupe sujet repris dans la question, le participe accordé avec lui]. */
const PHRASES_Q02 = [
    ['Cette pomme est très abimée mais reste mangeable.', '-able', 'la pomme', 'mangée'],
    ['Ce gâteau est un peu trop cuit mais reste mangeable.', '-able', 'le gâteau', 'mangé'],
    ['Ce vieux pain est un peu dur mais reste mangeable.', '-able', 'le pain', 'mangé'],
    ['Ce vieux livre est très abimé mais reste lisible.', '-ible', 'le livre', 'lu'],
    ['Cette lettre est toute froissée mais reste lisible.', '-ible', 'la lettre', 'lue'],
    ['Cette écriture est très petite mais reste lisible.', '-ible', 'l\'écriture', 'lue'],
    ['Cette eau est un peu trouble mais reste buvable.', '-able', 'l\'eau', 'bue'],
    ['Ce jus d\'orange est un peu acide mais reste buvable.', '-able', 'le jus d\'orange', 'bu'],
    ['Ce vieux vélo est très rouillé mais reste réparable.', '-able', 'le vélo', 'réparé'],
    ['Cette vieille montre est cassée mais reste réparable.', '-able', 'la montre', 'réparée'],
    ['Ce cahier est très froissé mais reste utilisable.', '-able', 'le cahier', 'utilisé'],
    ['Cette maison est très ancienne mais reste habitable.', '-able', 'la maison', 'habitée'],
    ['Ce canapé est très lourd mais reste transportable.', '-able', 'le canapé', 'transporté'],
    ['Ce terrain est très caillouteux mais reste cultivable.', '-able', 'le terrain', 'cultivé'],
    ['L\'air de cette ville est très pollué mais reste respirable.', '-able', 'l\'air', 'respiré'],
    ['Cette tache est très petite mais reste visible.', '-ible', 'la tache', 'vue'],
    ['Cette erreur est grave mais reste pardonnable.', '-able', 'l\'erreur', 'pardonnée'],
    ['Ce projet est très ambitieux mais reste réalisable.', '-able', 'le projet', 'réalisé'],
    ['Cette douleur est forte mais reste supportable.', '-able', 'la douleur', 'supportée'],
];
const genQ02 = () => {
    const [phrase, suffixe, sujet, participe] = randomChoice(PHRASES_Q02);
    verifier(phrase.endsWith(`${suffixe.slice(1)}.`) && phrase.includes(' mais reste '),
        `Q02 : le suffixe ${suffixe} après « mais reste » dans « ${phrase} »`);
    return qcm(`peut être ${participe}.`,
        [`ne doit pas être ${participe}.`, `est ${participe} avec plaisir.`, `est ${participe} à moitié.`],
        `<p>« ${phrase} »</p><p>L'utilisation du suffixe « ${suffixe} » signifie que ${sujet}…</p>`,
        L02);
};

/**
 * Q03 : Analyser la formation d'un mot à partir de son radical.
 * Item : « Un « vendeur » désigne quelqu'un qui fait l'action de « vendre ». Un seul des mots suivants est
 *   formé comme « vendeur » : lequel ? » ; malheur, chaleur, joueur (juste), longueur.
 * Domaine : un nom modèle en -eur formé sur un verbe (10 : vendeur, nageur, chanteur, danseur, coureur,
 *   menteur, chercheur, voyageur, promeneur, dormeur) ; le juste, un autre nom formé de même (22 noms :
 *   joueur, tricheur, marcheur, campeur, skieur…) ; deux noms de qualité formés sur un adjectif (13 :
 *   chaleur, longueur, largeur, hauteur, douceur…) ; un mot dont le -eur n'est pas un suffixe (malheur,
 *   bonheur, odeur, vapeur). 65 520 variantes. Écartés : couleur, humeur, fleur (couler, humer, fleurer :
 *   un faux verbe à démasquer, piège d'une autre nature) ; lecteur, conducteur (le verbe ne s'y lit pas).
 * Erreurs : malheur : la finale « -eur » entendue suffit, sans couper le mot (mal + heur) ; chaleur,
 *   longueur : « -eur » ajouté à un adjectif (chaud, long), le nom dit une qualité, pas quelqu'un qui fait
 *   une action. Les variantes gardent ces deux familles de pièges.
 */
const L03 = 'Analyser la formation d\'un mot à partir de son radical';
/** [nom, verbe] : le nom désigne quelqu'un qui fait l'action du verbe. */
const MODELES_Q03 = [
    ['vendeur', 'vendre'], ['nageur', 'nager'], ['chanteur', 'chanter'], ['danseur', 'danser'], ['coureur', 'courir'],
    ['menteur', 'mentir'], ['chercheur', 'chercher'], ['voyageur', 'voyager'], ['promeneur', 'promener'],
    ['dormeur', 'dormir'],
];
/** Formés comme le modèle : un verbe + -eur, quelqu'un qui fait l'action. */
const AGENTS_Q03 = [
    'joueur', 'nageur', 'chanteur', 'danseur', 'coureur', 'menteur', 'tricheur', 'marcheur', 'voyageur', 'promeneur',
    'pêcheur', 'campeur', 'grimpeur', 'plongeur', 'patineur', 'skieur', 'serveur', 'acheteur', 'visiteur', 'chercheur',
    'dormeur', 'vendeur',
];
/** Un adjectif + -eur : le nom d'une qualité, pas de quelqu'un. */
const QUALITES_Q03 = [
    'chaleur', 'longueur', 'largeur', 'hauteur', 'grandeur', 'blancheur', 'douceur', 'lenteur', 'épaisseur',
    'profondeur', 'rondeur', 'grosseur', 'minceur',
];
/** Une finale en -eur qui n'est pas un suffixe. */
const SANS_SUFFIXE_Q03 = ['malheur', 'bonheur', 'odeur', 'vapeur'];
const genQ03 = () => {
    const [modele, verbe] = randomChoice(MODELES_Q03);
    const juste = randomChoice(AGENTS_Q03.filter((nom) => nom !== modele));
    const faux = [...tirer(QUALITES_Q03, 2), randomChoice(SANS_SUFFIXE_Q03)];
    verifier([juste, ...faux].every((mot) => mot.endsWith('eur')), `Q03 : quatre mots en -eur (${juste}, ${faux.join(', ')})`);
    return qcm(juste, faux,
        `<p>Un « ${modele} » désigne quelqu'un qui fait l'action de « ${verbe} ».</p>`
        + `<p>Un seul des mots suivants est formé comme « ${modele} » : lequel ?</p>`,
        L03);
};

/**
 * Q04 : Identifier le radical d'une famille de mots.
 * Item : « Trouver le mot de la même famille que : « manuel, manucure, manuscrit ». » ; manoir, maitre,
 *   main (juste), maigre.
 * Domaine : trois mots formés sur un radical savant (manu-) et le mot courant qui en garde le sens mais pas
 *   tout à fait la forme (main) ; trois pièges qui commencent par les mêmes lettres, sans lien de sens.
 *   Familles : main (pièges tirés de manoir, maitre, maigre, manteau, maillot), mer (marin, maritime,
 *   marée), fleur (floral, floraison, flore), frère (fraternel, fraternité, fraterniser), air (aérien,
 *   aéroport, aération), soleil (solaire, parasol, tournesol), quatre pièges pour chacune ; 30 variantes.
 *   Chaque piège est vérifié hors de la famille (étymologie). Écartés : manche, manège, manette, manier
 *   (de main), piège et pieuvre (liés au pied), fratrie (venu du grec), sol (homonyme exact du radical).
 * Erreurs : manoir, maitre, maigre : la ressemblance des premières lettres (man-, mai-) prise pour une
 *   parenté, sans passer par le sens (manuel, manucure, manuscrit : ce qui se fait à la main). Les
 *   variantes : trois mots qui commencent comme la famille ou comme le juste (garde : deux lettres).
 */
const L04 = 'Identifier le radical d\'une famille de mots';
const FAMILLES_Q04 = [
    { mots: ['manuel', 'manucure', 'manuscrit'], juste: 'main', pieges: ['manoir', 'maitre', 'maigre', 'manteau', 'maillot'] },
    { mots: ['marin', 'maritime', 'marée'], juste: 'mer', pieges: ['mari', 'marcher', 'marron', 'merle'] },
    { mots: ['floral', 'floraison', 'flore'], juste: 'fleur', pieges: ['flocon', 'fleuve', 'flou', 'flèche'] },
    { mots: ['fraternel', 'fraternité', 'fraterniser'], juste: 'frère', pieges: ['fraise', 'frelon', 'frapper', 'frein'] },
    { mots: ['aérien', 'aéroport', 'aération'], juste: 'air', pieges: ['aigle', 'aiguille', 'aimant', 'aile'] },
    { mots: ['solaire', 'parasol', 'tournesol'], juste: 'soleil', pieges: ['soldat', 'solide', 'solution', 'solo'] },
];
const genQ04 = () => {
    const { mots, juste, pieges } = randomChoice(FAMILLES_Q04);
    const faux = tirer(pieges, 3);
    const debut = (mot) => nu(mot).slice(0, 2);
    verifier(faux.every((p) => [juste, ...mots].some((m) => debut(m) === debut(p))),
        `Q04 : des pièges qui commencent comme la famille de ${juste} (${faux.join(', ')})`);
    return qcm(juste, faux, `<p>Trouver le mot de la même famille que : « ${mots.join(', ')} ».</p>`, L04);
};

/**
 * Q05 : Déduire le sens d'un mot à partir de son radical.
 * Item : « Les noms « chronologie », « chronomètre » ou encore l'adjectif « chronique » viennent du mot
 *   grec : chronos = le temps. Une maladie chronique est donc une maladie qui… » ; dure plusieurs années.
 *   (juste), est en train de guérir., fait beaucoup souffrir., est extrêmement répandue.
 * Domaine : deux noms et un adjectif formés sur un radical grec ou latin dont le sens est donné, puis un
 *   groupe nominal avec l'adjectif à expliquer ; le juste applique le sens du radical. 15 radicaux, une
 *   variante chacun : chronos (l'item), pes, pedis (pédestre), aqua (aquatique), mikros (microscopique),
 *   autos (automatique), bis (bilingue), têle (télécommandé), hippos (hippique), audire (auditif), ager
 *   (agricole), unus (unicolore), zôon (zoologique), kardia (cardiaque), monos (monotone), visus
 *   (visuel). Écartés : aviaire (mot rare pour un élève de 6e : TRANCHÉ LE 11/10, même complexité que
 *   l'item), thermal (une source thermale n'est pas toujours chaude),
 *   tricolore (le drapeau : un sens appris, pas déduit), dentaire, lunaire (le mot français se lit dans
 *   le radical), manuel (famille de Q04), pédiatrique (même début que pédestre, sens différent),
 *   biologique (le sens « bio » d'aujourd'hui n'est plus « la vie »).
 * Erreurs : trois autres propriétés d'une maladie, sans lien avec le temps : son évolution (guérir), sa
 *   gravité (souffrir, la confusion courante entre « chronique » et « grave »), son étendue (répandue) ;
 *   l'élève qui les choisit n'applique pas le sens du radical. Les variantes : trois propriétés plausibles
 *   de la même chose, qui ne viennent pas du radical.
 */
const L05 = 'Déduire le sens d\'un mot à partir de son radical';
const RADICAUX_Q05 = [
    {
        noms: ['chronologie', 'chronomètre'], adjectif: 'chronique', origine: 'du mot grec : chronos = le temps',
        question: 'Une maladie chronique est donc une maladie qui…',
        juste: 'dure plusieurs années.', faux: ['est en train de guérir.', 'fait beaucoup souffrir.', 'est extrêmement répandue.'],
    },
    {
        noms: ['pédale', 'pédicure'], adjectif: 'pédestre', origine: 'du mot latin : pes, pedis = le pied',
        question: 'Une randonnée pédestre est donc une randonnée qui…',
        juste: 'se fait à pied.', faux: ['se fait en montagne.', 'dure plusieurs jours.', 'se fait en groupe.'],
    },
    {
        noms: ['aquarium', 'aquarelle'], adjectif: 'aquatique', origine: 'du mot latin : aqua = l\'eau',
        question: 'Un animal aquatique est donc un animal qui…',
        juste: 'vit dans l\'eau.', faux: ['vit en groupe.', 'vit très longtemps.', 'vit la nuit.'],
    },
    {
        noms: ['microscope', 'microbe'], adjectif: 'microscopique', origine: 'du mot grec : mikros = petit',
        question: 'Un animal microscopique est donc un animal qui…',
        juste: 'est minuscule.', faux: ['vit dans la mer.', 'se déplace très vite.', 'est dangereux.'],
    },
    {
        noms: ['automobile', 'autoportrait'], adjectif: 'automatique', origine: 'du mot grec : autos = soi-même',
        question: 'Une porte automatique est donc une porte qui…',
        juste: 's\'ouvre toute seule.', faux: ['s\'ouvre avec une clé.', 'est très lourde.', 'fait beaucoup de bruit.'],
    },
    {
        noms: ['bicyclette', 'binôme'], adjectif: 'bilingue', origine: 'du mot latin : bis = deux fois',
        question: 'Une personne bilingue est donc une personne qui…',
        juste: 'parle deux langues.', faux: ['parle très bien.', 'parle très vite.', 'parle beaucoup.'],
    },
    {
        noms: ['téléphone', 'télescope'], adjectif: 'télécommandé', origine: 'du mot grec : têle = loin',
        question: 'Un jouet télécommandé est donc un jouet qui…',
        juste: 'se commande à distance.', faux: ['se commande avec la voix.', 'va très vite.', 'se casse facilement.'],
    },
    {
        noms: ['hippodrome', 'hippopotame'], adjectif: 'hippique', origine: 'du mot grec : hippos = le cheval',
        question: 'Une course hippique est donc une course qui…',
        juste: 'se fait avec des chevaux.', faux: ['se fait dans l\'eau.', 'se fait en voiture.', 'se fait à pied.'],
    },
    {
        noms: ['audition', 'auditeur'], adjectif: 'auditif', origine: 'du mot latin : audire = entendre',
        question: 'Un appareil auditif est donc un appareil qui…',
        juste: 'aide à entendre.', faux: ['aide à voir.', 'aide à marcher.', 'aide à respirer.'],
    },
    {
        noms: ['agriculteur', 'agriculture'], adjectif: 'agricole', origine: 'du mot latin : ager, agri = le champ',
        question: 'Un engin agricole est donc un engin qui…',
        juste: 'sert à travailler les champs.',
        faux: ['sert à construire des routes.', 'sert à transporter des voyageurs.', 'sert à nettoyer les rues.'],
    },
    {
        noms: ['uniforme', 'unité'], adjectif: 'unicolore', origine: 'du mot latin : unus = un',
        question: 'Un tee-shirt unicolore est donc un tee-shirt qui…',
        juste: 'n\'a qu\'une seule couleur.', faux: ['a des rayures.', 'est très léger.', 'change de couleur au lavage.'],
    },
    {
        noms: ['zoo', 'zoologie'], adjectif: 'zoologique', origine: 'du mot grec : zôon = l\'animal',
        question: 'Un parc zoologique est donc un parc qui…',
        juste: 'présente des animaux.', faux: ['présente des plantes rares.', 'propose des manèges.', 'se trouve en ville.'],
    },
    {
        noms: ['cardiologue', 'cardiologie'], adjectif: 'cardiaque', origine: 'du mot grec : kardia = le cœur',
        question: 'Un problème cardiaque est donc un problème qui…',
        juste: 'touche le cœur.', faux: ['est très grave.', 'dure très longtemps.', 'touche beaucoup de gens.'],
    },
    {
        noms: ['monologue', 'monoski'], adjectif: 'monotone', origine: 'du mot grec : monos = un seul',
        question: 'Une voix monotone est donc une voix qui…',
        juste: 'reste toujours sur le même ton.', faux: ['est très forte.', 'tremble beaucoup.', 'est très douce.'],
    },
    {
        noms: ['vision', 'visibilité'], adjectif: 'visuel', origine: 'du mot latin : visus = la vue',
        question: 'Une mémoire visuelle est donc une mémoire qui…',
        juste: 'retient ce que l\'on voit.', faux: ['retient ce que l\'on entend.', 'dure très longtemps.', 'oublie très vite.'],
    },
];
const genQ05 = () => {
    const { noms, adjectif, origine, question, juste, faux } = randomChoice(RADICAUX_Q05);
    const debut = nu(adjectif).slice(0, 2);
    // L'adjectif peut être accordé dans la question (« visuel » → « une mémoire visuelle »).
    verifier(noms.every((n) => nu(n).slice(0, 2) === debut) && question.includes(` ${adjectif}`),
        `Q05 : un même radical pour ${noms.join(', ')}, ${adjectif}`);
    return qcm(juste, faux,
        `<p>Les noms « ${noms[0]} », « ${noms[1]} » ou encore l'adjectif « ${adjectif} » viennent ${origine}.</p>`
        + `<p>${question}</p>`,
        L05);
};

/**
 * Q06 : Identifier la famille d'un mot en contexte.
 * Item : « « Sa mère est très permissive : elle lui laisse faire ce qu'il veut. » D'après cette phrase,
 *   « permissive » est de la même famille que… » ; permanent., percevoir., permettre. (juste), perturbé.
 * Domaine : un adjectif dérivé d'un verbe, dont la forme s'écarte un peu du verbe (permiss- / permett-),
 *   dans une phrase dont la suite en donne le sens ; le juste est le verbe ; trois pièges commencent par
 *   les mêmes lettres sans en être (un autre radical derrière le même début). 11 phrases (permissive,
 *   destructeur, compréhensif, protecteur, persuasif, serviable, instructif, inventive, répétitif,
 *   combative, résistant), pièges tirés parmi quatre ; 38 variantes. Écarté : instrument (même origine
 *   latine qu'instruire).
 * Erreurs : permanent, percevoir, perturbé : le même début de mot (per-) pris pour une parenté ; la phrase,
 *   qui dit le sens (« laisse faire »), n'est pas utilisée. Les variantes : trois mots qui commencent par
 *   les mêmes lettres que le mot de la phrase (garde : trois lettres communes).
 */
const L06 = 'Identifier la famille d\'un mot en contexte';
const PHRASES_Q06 = [
    {
        phrase: 'Sa mère est très permissive : elle lui laisse faire ce qu\'il veut.', mot: 'permissive',
        juste: 'permettre', pieges: ['permanent', 'percevoir', 'perturbé'],
    },
    {
        phrase: 'Mon petit frère est très destructeur : il casse tous ses jouets.', mot: 'destructeur',
        juste: 'détruire', pieges: ['destin', 'dessiner', 'descendre', 'désert'],
    },
    {
        phrase: 'Notre professeur est très compréhensif : il accepte facilement nos excuses.', mot: 'compréhensif',
        juste: 'comprendre', pieges: ['compter', 'comparer', 'compagnon', 'complet'],
    },
    {
        phrase: 'Mon grand frère est très protecteur : il veille toujours sur moi.', mot: 'protecteur',
        juste: 'protéger', pieges: ['profiter', 'promener', 'provoquer', 'profond'],
    },
    {
        phrase: 'Ce vendeur est très persuasif : il nous a convaincus d\'acheter ce jeu.', mot: 'persuasif',
        juste: 'persuader', pieges: ['perdre', 'perroquet', 'personne', 'perle'],
    },
    {
        phrase: 'Notre voisin est très serviable : il nous aide toujours.', mot: 'serviable',
        juste: 'servir', pieges: ['serpent', 'serrer', 'série', 'sérieux'],
    },
    {
        phrase: 'Ce documentaire est très instructif : on y apprend beaucoup de choses.', mot: 'instructif',
        juste: 'instruire', pieges: ['installer', 'instant', 'insister', 'instinct'],
    },
    {
        phrase: 'Ma cousine est très inventive : elle imagine sans cesse de nouveaux jeux.', mot: 'inventive',
        juste: 'inventer', pieges: ['inviter', 'invisible', 'inverser', 'invincible'],
    },
    {
        phrase: 'Ce travail est très répétitif : on refait sans cesse les mêmes gestes.', mot: 'répétitif',
        juste: 'répéter', pieges: ['réparer', 'répondre', 'repas', 'répartir'],
    },
    {
        phrase: 'Cette joueuse est très combative : elle ne renonce jamais.', mot: 'combative',
        juste: 'combattre', pieges: ['combien', 'combler', 'combinaison'],
    },
    {
        phrase: 'Ce coureur est très résistant : il ne se fatigue presque jamais.', mot: 'résistant',
        juste: 'résister', pieges: ['réserver', 'résumer', 'résoudre', 'résultat'],
    },
];
const genQ06 = () => {
    const { phrase, mot, juste, pieges } = randomChoice(PHRASES_Q06);
    const faux = tirer(pieges, 3);
    verifier(phrase.includes(` ${mot} `) && faux.every((p) => nu(p).slice(0, 3) === nu(mot).slice(0, 3)),
        `Q06 : des pièges qui commencent comme « ${mot} » (${faux.join(', ')})`);
    return qcm(`${juste}.`, faux.map((p) => `${p}.`),
        `<p>« ${phrase} »</p><p>D'après cette phrase, « ${mot} » est de la même famille que…</p>`,
        L06);
};

/**
 * Q07 : Déduire le sens d'une expression figurée en contexte.
 * Item : « « Il a mangé trop de chocolat, il n'est pas dans son assiette. » L'expression « ne pas être
 *   dans son assiette » signifie… » ; avoir tout mangé., adorer le chocolat., être un peu malade. (juste),
 *   manger dans le plat.
 * Domaine : une expression figurée courante, dans une phrase qui permet d'en deviner le sens ; 20
 *   expressions (l'item, avoir une peur bleue, prendre ses jambes à son cou, avoir la langue bien pendue,
 *   donner sa langue au chat, passer une nuit blanche, avoir les yeux plus gros que le ventre, avoir un
 *   coup de foudre, se lever du pied gauche, vendre la mèche, avoir la tête dans les nuages, tomber dans
 *   les pommes, avoir un poil dans la main, mettre la main à la pâte, poser un lapin, avoir du pain sur
 *   la planche, avoir la main verte, être aux anges, avoir le cœur sur la main, tirer les vers du nez),
 *   une variante chacune. Écartés : coûter les yeux de la tête (orthographe rectifiée discutée), avoir la
 *   chair de poule (de froid ou de peur : deux sens), être dans la lune (doublon de la tête dans les
 *   nuages), être au paradis comme lecture mot à mot d'« être aux anges » (il veut dire aussi « heureux »).
 * Erreurs : « manger dans le plat » et « avoir tout mangé » : l'expression lue mot à mot (pas dans
 *   l'assiette, l'assiette vide) ; « adorer le chocolat » : une idée tirée de la phrase, pas le sens de
 *   l'expression. Les variantes : deux lectures mot à mot de l'expression et une idée tirée de la phrase.
 */
const L07 = 'Déduire le sens d\'une expression figurée en contexte';
const EXPRESSIONS_Q07 = [
    {
        phrase: 'Il a mangé trop de chocolat, il n\'est pas dans son assiette.', expression: 'ne pas être dans son assiette',
        juste: 'être un peu malade.', faux: ['avoir tout mangé.', 'adorer le chocolat.', 'manger dans le plat.'],
    },
    {
        phrase: 'En entendant le tonnerre, mon petit frère a eu une peur bleue.', expression: 'avoir une peur bleue',
        juste: 'avoir très peur.', faux: ['devenir tout bleu.', 'avoir très froid.', 'aimer les orages.'],
    },
    {
        phrase: 'Quand le chien s\'est mis à aboyer, le voleur a pris ses jambes à son cou.',
        expression: 'prendre ses jambes à son cou',
        juste: 's\'enfuir en courant.', faux: ['se faire mal au cou.', 'faire de la gymnastique.', 'avoir peur des chiens.'],
    },
    {
        phrase: 'Pendant tout le repas, ma cousine a raconté ses vacances : elle a la langue bien pendue.',
        expression: 'avoir la langue bien pendue',
        juste: 'parler beaucoup.', faux: ['tirer la langue.', 'avoir mal à la langue.', 'aimer les vacances.'],
    },
    {
        phrase: 'Il ne trouvait pas la réponse de la devinette, alors il a donné sa langue au chat.',
        expression: 'donner sa langue au chat',
        juste: 'renoncer à deviner.', faux: ['donner à manger au chat.', 'tirer la langue.', 'adorer les devinettes.'],
    },
    {
        phrase: 'L\'orage a duré jusqu\'au matin : j\'ai passé une nuit blanche.', expression: 'passer une nuit blanche',
        juste: 'ne pas dormir de la nuit.',
        faux: ['dormir dans des draps blancs.', 'voir tomber la neige.', 'avoir peur de l\'orage.'],
    },
    {
        phrase: 'Il s\'est servi trois parts de gâteau sans pouvoir les finir : il a eu les yeux plus gros que le ventre.',
        expression: 'avoir les yeux plus gros que le ventre',
        juste: 'se servir plus qu\'on ne peut manger.',
        faux: ['avoir de très grands yeux.', 'avoir mal au ventre.', 'adorer le gâteau.'],
    },
    {
        phrase: 'Au refuge, ma sœur a eu un coup de foudre pour un petit chat.', expression: 'avoir un coup de foudre',
        juste: 'aimer tout de suite.', faux: ['être frappé par la foudre.', 'avoir peur de l\'orage.', 'adorer les animaux.'],
    },
    {
        phrase: 'Ce matin, il s\'est disputé avec tout le monde : il s\'est levé du pied gauche.',
        expression: 'se lever du pied gauche',
        juste: 'être de mauvaise humeur.',
        faux: ['se lever en boitant.', 'mettre ses chaussures à l\'envers.', 'aimer se disputer.'],
    },
    {
        phrase: 'Mon frère a vendu la mèche : maman sait tout de la fête surprise.', expression: 'vendre la mèche',
        juste: 'révéler un secret.', faux: ['vendre des bougies.', 'se couper une mèche de cheveux.', 'organiser une fête.'],
    },
    {
        phrase: 'Il a oublié son cartable à la maison : il a vraiment la tête dans les nuages.',
        expression: 'avoir la tête dans les nuages',
        juste: 'être distrait.', faux: ['regarder le ciel.', 'être très grand.', 'ne pas aimer l\'école.'],
    },
    {
        phrase: 'Il faisait si chaud dans le bus qu\'une dame est tombée dans les pommes.', expression: 'tomber dans les pommes',
        juste: 's\'évanouir.', faux: ['tomber d\'un pommier.', 'glisser sur une pomme.', 'avoir très soif.'],
    },
    {
        phrase: 'Il ne range jamais sa chambre : il a un poil dans la main.', expression: 'avoir un poil dans la main',
        juste: 'être paresseux.', faux: ['avoir la main poilue.', 'se faire mal à la main.', 'aimer le désordre.'],
    },
    {
        phrase: 'Pour préparer la fête de l\'école, tout le monde a mis la main à la pâte.',
        expression: 'mettre la main à la pâte',
        juste: 'participer au travail.', faux: ['faire un gâteau.', 'se salir les mains.', 'aimer les fêtes.'],
    },
    {
        phrase: 'Mon ami devait me retrouver devant le cinéma, mais il m\'a posé un lapin.', expression: 'poser un lapin',
        juste: 'ne pas venir au rendez-vous.', faux: ['offrir un lapin.', 'courir très vite.', 'aimer le cinéma.'],
    },
    {
        phrase: 'Avec tous ces exercices à finir pour demain, j\'ai du pain sur la planche.',
        expression: 'avoir du pain sur la planche',
        juste: 'avoir beaucoup de travail.',
        faux: ['préparer des tartines.', 'travailler dans une boulangerie.', 'aimer faire ses devoirs.'],
    },
    {
        phrase: 'Les tomates de mon grand-père sont superbes : il a la main verte.', expression: 'avoir la main verte',
        juste: 'savoir faire pousser les plantes.', faux: ['avoir les mains sales.', 'peindre en vert.', 'aimer les tomates.'],
    },
    {
        phrase: 'Mon frère a reçu le vélo de ses rêves : il est aux anges.', expression: 'être aux anges',
        juste: 'être très heureux.', faux: ['voir des anges.', 'savoir voler.', 'aimer le vélo.'],
    },
    {
        phrase: 'Ma grand-mère partage tout ce qu\'elle a : elle a le cœur sur la main.',
        expression: 'avoir le cœur sur la main',
        juste: 'être généreux.', faux: ['avoir mal au cœur.', 'se tenir par la main.', 'être très riche.'],
    },
    {
        phrase: 'Ma mère a fini par me tirer les vers du nez : elle sait tout sur ma mauvaise note.',
        expression: 'tirer les vers du nez',
        juste: 'faire avouer quelque chose.', faux: ['se moucher.', 'soigner un rhume.', 'gronder un enfant.'],
    },
];
const genQ07 = () => {
    const { phrase, expression, juste, faux } = randomChoice(EXPRESSIONS_Q07);
    const dernierMot = expression.split(' ').pop();
    verifier(phrase.includes(dernierMot), `Q07 : l'expression « ${expression} » dans « ${phrase} »`);
    return qcm(juste, faux, `<p>« ${phrase} »</p><p>L'expression « ${expression} » signifie…</p>`, L07);
};

/**
 * Q08 : Inférer le sens d'un mot en contexte.
 * Item : « « Julie trouve ce plat succulent. Elle en a repris deux fois. » Dans cette phrase,
 *   « succulent » est le synonyme de… » ; délicieux. (juste), indigeste., répugnant., écœurant.
 *   « Elle en a repris » devient « … et en a repris » : pas de pronom pour une personne nommée.
 * Domaine : un adjectif peu courant en 6e, que la suite de la phrase permet de comprendre ; le juste est
 *   son synonyme, accordé ; 20 adjectifs (succulent, palpitant, ardu, affable, mélodieuse, périlleux,
 *   docile, hilarante, loquace, lugubre, encombrant, exquise, insipide, craintif, rêche, copieux, escarpé,
 *   limpide, poignant, strident), une variante chacun. Le prénom n'est jamais repris par un pronom.
 *   Écartés : exiguë (orthographe rectifiée « exigüe »), espiègle (la phrase demandait un pronom).
 * Erreurs : indigeste, répugnant, écœurant : des mots du même domaine (un plat), de sens contraire :
 *   l'élève qui ne connaît pas « succulent » n'utilise pas l'indice de la phrase (« en a repris deux
 *   fois »). Les variantes : trois mots du même domaine, de sens contraire ou étranger au juste.
 */
const L08 = 'Inférer le sens d\'un mot en contexte';
const MOTS_Q08 = [
    {
        phrase: 'Julie trouve ce plat succulent et en a repris deux fois.', mot: 'succulent',
        juste: 'délicieux.', faux: ['indigeste.', 'répugnant.', 'écœurant.'],
    },
    {
        phrase: 'Léo trouve ce roman palpitant et ne peut plus s\'arrêter de le lire.', mot: 'palpitant',
        juste: 'passionnant.', faux: ['ennuyeux.', 'lassant.', 'monotone.'],
    },
    {
        phrase: 'Tom trouve cet exercice ardu et demande de l\'aide.', mot: 'ardu',
        juste: 'difficile.', faux: ['facile.', 'simple.', 'rapide.'],
    },
    {
        phrase: 'Lina trouve sa nouvelle voisine affable et l\'invite souvent.', mot: 'affable',
        juste: 'aimable.', faux: ['désagréable.', 'méchante.', 'impolie.'],
    },
    {
        phrase: 'Hugo trouve cette chanson mélodieuse et la fredonne toute la journée.', mot: 'mélodieuse',
        juste: 'harmonieuse.', faux: ['criarde.', 'bruyante.', 'assourdissante.'],
    },
    {
        phrase: 'Nathan trouve ce chemin périlleux et préfère faire demi-tour.', mot: 'périlleux',
        juste: 'dangereux.', faux: ['sûr.', 'tranquille.', 'agréable.'],
    },
    {
        phrase: 'Chloé trouve ce chien docile et le promène sans laisse.', mot: 'docile',
        juste: 'obéissant.', faux: ['agressif.', 'désobéissant.', 'turbulent.'],
    },
    {
        phrase: 'Adam trouve cette histoire hilarante et rit aux éclats.', mot: 'hilarante',
        juste: 'comique.', faux: ['triste.', 'effrayante.', 'ennuyeuse.'],
    },
    {
        phrase: 'Noé trouve son voisin de table loquace et voudrait un peu de silence.', mot: 'loquace',
        juste: 'bavard.', faux: ['silencieux.', 'timide.', 'discret.'],
    },
    {
        phrase: 'Lou trouve cette vieille cave lugubre et n\'ose pas y descendre.', mot: 'lugubre',
        juste: 'sinistre.', faux: ['gaie.', 'joyeuse.', 'accueillante.'],
    },
    {
        phrase: 'Rayan trouve ce sac encombrant et ne peut pas le ranger dans son casier.', mot: 'encombrant',
        juste: 'volumineux.', faux: ['pratique.', 'léger.', 'petit.'],
    },
    {
        phrase: 'Inès trouve cette tarte exquise et en reprend une part.', mot: 'exquise',
        juste: 'délicieuse.', faux: ['immangeable.', 'infecte.', 'écœurante.'],
    },
    {
        phrase: 'Max trouve cette soupe insipide et ajoute du sel.', mot: 'insipide',
        juste: 'fade.', faux: ['délicieuse.', 'épicée.', 'savoureuse.'],
    },
    {
        phrase: 'Sami trouve ce chaton craintif et le voit se cacher sous le lit au moindre bruit.', mot: 'craintif',
        juste: 'peureux.', faux: ['courageux.', 'joueur.', 'agressif.'],
    },
    {
        phrase: 'Maya trouve ce pull rêche et se gratte sans arrêt en le portant.', mot: 'rêche',
        juste: 'rugueux.', faux: ['doux.', 'chaud.', 'léger.'],
    },
    {
        phrase: 'Yanis trouve ce repas copieux et ne peut pas tout finir.', mot: 'copieux',
        juste: 'abondant.', faux: ['léger.', 'maigre.', 'petit.'],
    },
    {
        phrase: 'Clara trouve ce sentier escarpé et monte lentement.', mot: 'escarpé',
        juste: 'raide.', faux: ['plat.', 'large.', 'court.'],
    },
    {
        phrase: 'Nina trouve cette eau limpide et voit le fond de la rivière.', mot: 'limpide',
        juste: 'transparente.', faux: ['trouble.', 'sale.', 'boueuse.'],
    },
    {
        phrase: 'Aya trouve ce film poignant et pleure à la fin.', mot: 'poignant',
        juste: 'émouvant.', faux: ['drôle.', 'ennuyeux.', 'joyeux.'],
    },
    {
        phrase: 'Liam trouve ce bruit strident et se bouche les oreilles.', mot: 'strident',
        juste: 'perçant.', faux: ['doux.', 'faible.', 'agréable.'],
    },
];
const genQ08 = () => {
    const { phrase, mot, juste, faux } = randomChoice(MOTS_Q08);
    verifier(phrase.includes(` ${mot} `), `Q08 : « ${mot} » dans « ${phrase} »`);
    return qcm(juste, faux, `<p>« ${phrase} »</p><p>Dans cette phrase, « ${mot} » est le synonyme de…</p>`, L08);
};

/**
 * Q09 : Identifier le sens d'un mot polysémique en contexte.
 * Item : « Dans quelle phrase le verbe « donner » signifie-t-il « offrir, remettre » ? » ; Ma chambre
 *   donne sur le jardin., Louise donne son bonnet à Sarah. (juste), On donne une pièce de Molière ce
 *   soir., Je ne lui donne pas trente ans. La base coupe les phrases (« Ma chambre donne sur… . ») : la
 *   capture fait foi.
 * Domaine : un verbe très courant, son sens premier (le juste) et trois autres sens dans des phrases
 *   simples ; 12 verbes (donner, prendre, passer, tirer, tenir, porter, tourner, rendre, toucher, servir,
 *   battre, pousser), quatre autres sens pour donner, passer et tenir ; 21 variantes. Écartés : prendre
 *   froid (« attraper » froid rejoint le sens cherché), monter et lever (leurs autres sens montent aussi),
 *   sentir (deux sens d'odeur).
 * Erreurs : les trois autres sens de « donner » (avoir vue sur, jouer un spectacle, attribuer un âge) :
 *   l'élève reconnaît le verbe, pas son sens dans la phrase. Les variantes : trois autres sens du verbe,
 *   chacun fixé par sa construction.
 */
const L09 = 'Identifier le sens d\'un mot polysémique en contexte';
/** Un verbe, le sens cherché, les débuts de ses formes (garde), la phrase juste, les phrases d'un autre sens. */
const VERBES_Q09 = [
    {
        verbe: 'donner', sens: 'offrir, remettre', formes: ['donn'], juste: 'Louise donne son bonnet à Sarah.',
        faux: ['Ma chambre donne sur le jardin.', 'On donne une pièce de Molière ce soir.', 'Je ne lui donne pas trente ans.',
            'Ce pommier donne beaucoup de fruits.'],
    },
    {
        verbe: 'prendre', sens: 'saisir, attraper', formes: ['pren', 'pris'], juste: 'Lucas prend un stylo dans sa trousse.',
        faux: ['Ce gâteau prend une heure à cuire.', 'Le feu a pris dans la cheminée.', 'Prenez la deuxième rue à gauche.'],
    },
    {
        verbe: 'passer', sens: 'donner, tendre', formes: ['pass'], juste: 'Nina passe le sel à son frère.',
        faux: ['Les vacances passent trop vite.', 'Mon cousin passe son brevet en juin.', 'Le facteur passe tous les matins.',
            'Ce film passe à la télévision ce soir.'],
    },
    {
        verbe: 'tirer', sens: 'attirer vers soi', formes: ['tir'], juste: 'Emma tire la porte pour l\'ouvrir.',
        faux: ['Le joueur tire au but.', 'Nous avons tiré au sort le gagnant.', 'Ce bleu tire sur le vert.'],
    },
    {
        verbe: 'tenir', sens: 'garder dans sa main', formes: ['tien', 'ten'], juste: 'Max tient la main de sa petite sœur.',
        faux: ['Ce vieux pont tient encore.', 'Toutes mes affaires tiennent dans ce sac.', 'Ma sœur tient beaucoup à son chat.',
            'Mon frère tient de notre père.'],
    },
    {
        verbe: 'porter', sens: 'être vêtu de', formes: ['port'], juste: 'Léa porte une robe rouge.',
        faux: ['Ce pommier porte beaucoup de fruits.', 'La discussion porte sur les vacances.', 'Sa voix porte très loin.'],
    },
    {
        verbe: 'tourner', sens: 'changer de direction', formes: ['tourn'], juste: 'Au carrefour, le bus tourne à droite.',
        faux: ['Le lait a tourné.', 'On tourne un film dans notre rue.', 'J\'ai la tête qui tourne.'],
    },
    {
        verbe: 'rendre', sens: 'redonner, rapporter', formes: ['rend'], juste: 'Rose rend le livre à la bibliothèque.',
        faux: ['Cette histoire me rend triste.', 'Nous nous rendons à la piscine.', 'Le chevalier vaincu s\'est rendu.'],
    },
    {
        verbe: 'toucher', sens: 'poser la main sur', formes: ['touch'], juste: 'Mia touche le pelage du chat.',
        faux: ['Ce film m\'a beaucoup touché.', 'La flèche a touché la cible.', 'Mon frère touche son premier salaire.'],
    },
    {
        verbe: 'servir', sens: 'donner à manger ou à boire', formes: ['ser'], juste: 'Mon père sert la soupe.',
        faux: ['Cette pochette sert à ranger mes crayons.', 'Au tennis, c\'est à Jade de servir.', 'Ce vieux drap sert de nappe.'],
    },
    {
        verbe: 'battre', sens: 'vaincre, gagner contre', formes: ['bat'], juste: 'Notre équipe a battu celle du collège voisin.',
        faux: ['Mon cœur bat très vite.', 'Le cuisinier bat les œufs en neige.', 'La pluie bat contre les vitres.'],
    },
    {
        verbe: 'pousser', sens: 'déplacer en appuyant', formes: ['pouss'], juste: 'Léo pousse le chariot jusqu\'à la caisse.',
        faux: ['Les tomates poussent bien cette année.', 'Le bébé pousse un petit cri.', 'Mes amis m\'ont poussé à chanter.'],
    },
];
const genQ09 = () => {
    const { verbe, sens, formes, juste, faux } = randomChoice(VERBES_Q09);
    const autres = tirer(faux, 3);
    verifier([juste, ...autres].every((p) => formes.some((f) => nu(p).includes(f))),
        `Q09 : le verbe « ${verbe} » dans chaque phrase`);
    return qcm(juste, autres, `<p>Dans quelle phrase le verbe « ${verbe} » signifie-t-il « ${sens} » ?</p>`, L09);
};

/**
 * Q10 : Choisir le mot approprié en contexte.
 * Item : « « Marie n'a pas … nécessaire de nous prévenir. » « Le délinquant va être … en comparution
 *   immédiate. » Les deux phrases peuvent être complétées par le même mot. Choisir celui qui convient. » ;
 *   jugé (juste), convoqué, estimé, pensé.
 * Domaine : un mot à deux sens, chacun appelé par une des deux phrases ; chaque piège ne va que dans une
 *   phrase. 16 mots (jugé, passer, touché, pris, glissé, frappé, battu, lancé, suivi, manqué, tenu,
 *   marché, posé, levé, couru, sauté), une variante chacun ; chaque piège a été essayé dans les deux
 *   phrases. Écartés : partie (va dans les deux phrases de « pièce »), laissé, atteint, heurté, descendu,
 *   poursuivi, rejoint, corrigé, recopié (deux phrases), mélangé (« les œufs en neige » peut le suivre),
 *   demandé (« demander une question » est fautif), baissé, foncé (forcé ou familier).
 * Erreurs : estimé et pensé ne vont que dans la première phrase (« juger nécessaire »), convoqué que dans
 *   la seconde : l'élève qui les choisit n'a vérifié qu'une phrase. Les variantes : des pièges qui
 *   complètent une phrase sur deux.
 */
const L10 = 'Choisir le mot approprié en contexte';
const PHRASES_Q10 = [
    {
        phrases: ['Marie n\'a pas … nécessaire de nous prévenir.', 'Le délinquant va être … en comparution immédiate.'],
        juste: 'jugé', faux: ['convoqué', 'estimé', 'pensé'],
    },
    {
        phrases: ['Peux-tu me … le sel ?', 'Nous allons … nos vacances à la mer.'],
        juste: 'passer', faux: ['donner', 'tendre', 'organiser'],
    },
    {
        phrases: ['Son histoire m\'a beaucoup … quand je l\'ai lue.', 'Le ballon a … le poteau.'],
        juste: 'touché', faux: ['ému', 'attristé', 'effleuré'],
    },
    {
        phrases: ['Le voleur a été … par la police.', 'Ce travail m\'a … toute la journée.'],
        juste: 'pris', faux: ['attrapé', 'arrêté', 'occupé'],
    },
    {
        phrases: ['Mon frère a … sur le verglas.', 'J\'ai … un mot dans ta poche.'],
        juste: 'glissé', faux: ['dérapé', 'mis', 'caché'],
    },
    {
        phrases: ['On a … à la porte.', 'Cette nouvelle m\'a beaucoup … ce matin.'],
        juste: 'frappé', faux: ['cogné', 'étonné', 'surpris'],
    },
    {
        phrases: ['Notre équipe a … les champions.', 'Le cuisinier a … les œufs en neige.'],
        juste: 'battu', faux: ['vaincu', 'dominé', 'monté'],
    },
    {
        phrases: ['Le joueur a … le ballon.', 'Le maire a … la fête du village.'],
        juste: 'lancé', faux: ['jeté', 'envoyé', 'ouvert'],
    },
    {
        phrases: ['Le chien a … son maître jusqu\'à la maison.', 'Nous avons … un cours de dessin.'],
        juste: 'suivi', faux: ['accompagné', 'pris', 'commencé'],
    },
    {
        phrases: ['J\'ai … le bus ce matin.', 'Tu nous as beaucoup … pendant les vacances.'],
        juste: 'manqué', faux: ['raté', 'écrit', 'téléphoné'],
    },
    {
        phrases: ['Mon frère a … sa promesse.', 'Ce vieux pont a … malgré la tempête.'],
        juste: 'tenu', faux: ['respecté', 'résisté', 'duré'],
    },
    {
        phrases: ['Nous avons … jusqu\'au collège.', 'Ton idée a très bien … auprès de mes parents.'],
        juste: 'marché', faux: ['couru', 'fonctionné', 'réussi'],
    },
    {
        phrases: ['Le professeur a … une question difficile.', 'L\'avion s\'est … sur la piste.'],
        juste: 'posé', faux: ['lu', 'écrit', 'arrêté'],
    },
    {
        phrases: ['Le soleil s\'est … à six heures.', 'J\'ai … la main pour poser une question.'],
        juste: 'levé', faux: ['couché', 'agité', 'tendu'],
    },
    {
        phrases: ['Nous avons … jusqu\'à la gare.', 'Le pompier a … un grand danger.'],
        juste: 'couru', faux: ['marché', 'roulé', 'évité'],
    },
    {
        phrases: ['Le chat a … sur la table.', 'J\'ai … le repas de midi.'],
        juste: 'sauté', faux: ['grimpé', 'bondi', 'oublié'],
    },
];
const genQ10 = () => {
    const { phrases, juste, faux } = randomChoice(PHRASES_Q10);
    verifier(phrases.length === 2 && phrases.every((p) => p.split('…').length === 2), `Q10 : un trou par phrase (${juste})`);
    return qcm(juste, faux,
        `<p>« ${phrases[0]} »</p><p>« ${phrases[1]} »</p>`
        + '<p>Les deux phrases peuvent être complétées par le même mot.</p><p>Choisir celui qui convient.</p>',
        L10);
};

/**
 * Q11 : Distinguer des nuances de sens en contexte d'un mot polysémique.
 * Item : « Choisir la signification de « peine » ou de l'expression formée avec « peine » dans chaque
 *   phrase. » ; tableau à trois colonnes, Idée de chagrin, Idée de difficulté, Idée de justesse :
 *   « Il éprouve de la peine à parler. » (difficulté), « Ce que tu lui as dit lui a fait beaucoup de
 *   peine. » (chagrin), « Ils ont eu à peine le temps de finir. » (justesse), « Elle peine à faire ses
 *   exercices. » (difficulté) ; « peine » est souligné dans chaque phrase.
 * Domaine : les colonnes et les quatre rôles de l'item, une phrase tirée par rôle, dans un ordre tiré : le
 *   nom au sens de chagrin (faire, avoir de la peine : 7 phrases), le nom au sens de difficulté (avoir,
 *   éprouver de la peine à : 6), le verbe peiner (6), la locution à peine (7) ; 1 764 variantes. La
 *   colonne de chaque phrase est relue sur sa construction (garde). Écartés : sans peine, avec peine, ce
 *   n'est pas la peine, valoir la peine, la peine de prison (un autre sens ou une autre colonne
 *   discutable), « à peine… que » (l'idée de temps s'y mêle). TRANCHÉ : le soulignement n'est pas repris,
 *   une ligne de tableau s'affiche en texte simple ; chaque phrase n'a qu'un « peine », que la consigne
 *   nomme.
 * Erreurs : « éprouver de la peine » fait lire du chagrin, « à parler » dit la difficulté ; le verbe
 *   « peiner », que l'élève qui ne connaît que le nom ne reconnaît pas ; « à peine », locution, rangée avec
 *   le nom. Les variantes gardent les quatre rôles, donc les trois pièges.
 * Trois colonnes qui ne sont pas Vrai / Faux : le tableau est déclaré à colonnes libres (FORMATS § 4 et
 *   4bis), comme l'item officiel relu depuis la base ; makeVraiFaux construit les lignes et la réponse.
 */
const L11 = 'Distinguer des nuances de sens en contexte d\'un mot polysémique';
const COLONNES_Q11 = ['Idée de chagrin', 'Idée de difficulté', 'Idée de justesse'];
/** Le nom au sens de chagrin : faire de la peine, avoir de la peine (sans « à » + infinitif). */
const CHAGRIN_Q11 = [
    'Ce que tu lui as dit lui a fait beaucoup de peine.',
    'Le départ de son meilleur ami lui fait de la peine.',
    'Ses paroles méchantes m\'ont fait de la peine.',
    'Voir mon petit frère pleurer me fait de la peine.',
    'Mamie a beaucoup de peine depuis le départ de sa voisine.',
    'Cette triste nouvelle nous a fait beaucoup de peine.',
    'Il a eu de la peine en quittant ses amis.',
];
/** Le nom au sens de difficulté : avoir, éprouver de la peine à + infinitif. */
const DIFFICULTE_NOM_Q11 = [
    'Il éprouve de la peine à parler.',
    'Mon grand-père a de la peine à monter les escaliers.',
    'Elle a eu de la peine à trouver notre maison.',
    'J\'ai de la peine à lire ton écriture.',
    'Après sa chute, il avait de la peine à marcher.',
    'Nous éprouvons de la peine à comprendre cette règle.',
];
/** Le verbe peiner, au présent : avoir du mal. */
const DIFFICULTE_VERBE_Q11 = [
    'Elle peine à faire ses exercices.',
    'Le cycliste peine dans la montée.',
    'Mon petit frère peine à lacer ses chaussures.',
    'Ce vieux camion peine dans la côte.',
    'Le randonneur peine sous la chaleur.',
    'Ma tante peine à ouvrir ce bocal.',
];
/** La locution à peine : tout juste, presque pas. */
const JUSTESSE_Q11 = [
    'Ils ont eu à peine le temps de finir.',
    'J\'ai eu à peine le temps de manger.',
    'Il reste à peine dix minutes avant la sonnerie.',
    'On entendait à peine la voix du professeur.',
    'Il restait à peine une place dans le bus.',
    'Ce sac pèse à peine deux kilos.',
    'La bouteille est à peine entamée.',
];
/** La colonne d'une phrase, relue sur sa construction (null : construction inconnue). */
const colonneQ11 = (phrase) => {
    const t = ` ${nu(phrase)} `;
    if (/\sa peine\s/.test(t)) return 2;
    if (/\sde la peine a\s/.test(t)) return 1;
    if (/\speine\s(a|dans|sous)\s/.test(t) && !/\s(la|de|a)\speine\s/.test(t)) return 1;
    if (/\sde peine[\s.]/.test(t) || /\sde la peine[\s.]/.test(t)) return 0;
    return null;
};
const genQ11 = () => {
    const tirage = shuffle([
        [randomChoice(CHAGRIN_Q11), 0],
        [randomChoice(DIFFICULTE_NOM_Q11), 1],
        [randomChoice(DIFFICULTE_VERBE_Q11), 1],
        [randomChoice(JUSTESSE_Q11), 2],
    ]);
    tirage.forEach(([phrase, colonne]) => verifier(colonneQ11(phrase) === colonne,
        `Q11 : « ${phrase} » relue en colonne ${colonneQ11(phrase)} au lieu de ${colonne}`));
    const payload = tableau(tirage.map(([phrase]) => phrase), tirage.map(([, colonne]) => colonne), COLONNES_Q11,
        '<p>Choisir la signification de « peine » ou de l\'expression formée avec « peine » dans chaque phrase.</p>', L11);
    payload.elements_additionnels.elements.forEach((element) => {
        if (element.type === 'table') element.config.type = 'colonnesLibres';
    });
    return payload;
};

/**
 * Q12 : Nommer un champ lexical.
 * Item : « « futur, prochain, prévoir, bientôt ». Ces mots appartiennent au thème de… » ; l'avenir.
 *   (juste), la conjugaison., la patience., l'imprudence.
 * Domaine : quatre mots, de natures variées, d'un même thème, qui n'est pas dans la liste ; 15 thèmes
 *   (l'avenir, la joie, la météo, la peur, la mer, l'école, la musique, la nuit, l'hiver, l'amitié, la
 *   peinture, l'anniversaire, la montagne, l'électricité, la pêche), une variante chacun. Les choix
 *   suivent « de » : tous en « la » ou « l' » (garde). Écartés : le passé, le voyage, le sommeil (« de le »
 *   ne se dit pas), la santé (le contraire de la maladie couvre les mêmes mots), l'histoire (couvre le
 *   passé), la justice (la police couvre trop de mots), la cuisine (la boulangerie aussi).
 * Erreurs : la conjugaison : un seul mot, « futur », pris dans un autre domaine où il a un sens ; la
 *   patience : une idée associée au thème (attendre ce qui arrive bientôt) ; l'imprudence : le contraire de
 *   ce qu'évoque un mot (prévoir). Les variantes reprennent ces pièges : un mot de la liste qui a un sens
 *   dans un autre domaine (enchanté, souffler, naviguer, réviser, note, étoile, gant, complice, toile,
 *   sommet, courant, canne), une idée associée, et le plus souvent un contraire.
 */
const L12 = 'Nommer un champ lexical';
const THEMES_Q12 = [
    { mots: 'futur, prochain, prévoir, bientôt', juste: 'l\'avenir.', faux: ['la conjugaison.', 'la patience.', 'l\'imprudence.'] },
    { mots: 'rire, ravi, enchanté, joyeusement', juste: 'la joie.', faux: ['la magie.', 'l\'amitié.', 'la tristesse.'] },
    { mots: 'tempête, pluvieux, nuage, souffler', juste: 'la météo.', faux: ['la respiration.', 'la peur.', 'la sécheresse.'] },
    { mots: 'trembler, effrayé, terreur, panique', juste: 'la peur.', faux: ['la fièvre.', 'la colère.', 'la bravoure.'] },
    { mots: 'vague, marée, plage, naviguer', juste: 'la mer.', faux: ['l\'informatique.', 'la natation.', 'la montagne.'] },
    { mots: 'cartable, élève, réviser, apprendre', juste: 'l\'école.', faux: ['la mécanique.', 'la curiosité.', 'la paresse.'] },
    { mots: 'mélodie, chanter, note, orchestre', juste: 'la musique.', faux: ['l\'école.', 'la joie.', 'la peinture.'] },
    { mots: 'obscurité, étoile, nocturne, dormir', juste: 'la nuit.', faux: ['la célébrité.', 'la peur.', 'la journée.'] },
    { mots: 'neige, glacial, gant, frissonner', juste: 'l\'hiver.', faux: ['la boxe.', 'la peur.', 'l\'été.'] },
    { mots: 'copain, complice, partager, ensemble', juste: 'l\'amitié.', faux: ['la police.', 'la générosité.', 'la solitude.'] },
    { mots: 'pinceau, toile, couleur, peindre', juste: 'la peinture.', faux: ['la couture.', 'la patience.', 'la sculpture.'] },
    { mots: 'bougie, cadeau, souffler, fêter', juste: 'l\'anniversaire.', faux: ['la respiration.', 'la surprise.', 'la tristesse.'] },
    { mots: 'sommet, escalader, altitude, randonneur', juste: 'la montagne.', faux: ['la géométrie.', 'la fatigue.', 'la plaine.'] },
    { mots: 'prise, courant, ampoule, brancher', juste: 'l\'électricité.', faux: ['la rivière.', 'la lumière.', 'la panne.'] },
    { mots: 'canne, hameçon, poisson, appât', juste: 'la pêche.', faux: ['la vieillesse.', 'la patience.', 'la cuisine.'] },
];
const genQ12 = () => {
    const { mots, juste, faux } = randomChoice(THEMES_Q12);
    verifier(mots.split(', ').length === 4 && [juste, ...faux].every((c) => /^(la |l')/.test(c)),
        `Q12 : quatre mots et des choix en « la » ou « l' » (${mots})`);
    return qcm(juste, faux, `<p>« ${mots} ».</p><p>Ces mots appartiennent au thème de…</p>`, L12);
};

/**
 * Q13 : Identifier un champ lexical en contexte.
 * Item : « « Ce lieu était immense et contenait aussi bien des essais que des romans. Il aimait s'y rendre
 *   pour feuilleter divers ouvrages, découvrir des personnages, se passionner pour des intrigues. » Le lieu
 *   évoqué par ces phrases est… » ; une bibliothèque. (juste), un laboratoire., un musée., un théâtre.
 * Domaine : deux phrases qui décrivent un lieu sans le nommer, par les mots de son champ lexical ; seul le
 *   juste les accueille tous ; 17 lieux (bibliothèque, gare, hôpital, stade, zoo, supermarché, piscine,
 *   musée, plage, boulangerie, cinéma, aéroport, salle de classe, garage, ferme, théâtre, église), une
 *   variante chacun. Écartés : la forêt (un parc a aussi chênes et sapins), le restaurant (un café a aussi
 *   serveurs et tables), la cuisine (une boulangerie a aussi four, farine et œufs), la station-service
 *   comme piège du garage (on peut y réparer).
 * Erreurs : un laboratoire (« essais » pris au sens d'expériences), un musée (un lieu immense où l'on
 *   découvre des œuvres), un théâtre (personnages, intrigues) : l'élève s'arrête à un ou deux mots, pas à
 *   l'ensemble. Les variantes : trois lieux appelés chacun par un ou deux mots du texte (quai, wagons ;
 *   chambres, couloirs ; rayons, caisse, panier ; tableaux, salle…).
 */
const L13 = 'Identifier un champ lexical en contexte';
const LIEUX_Q13 = [
    {
        texte: 'Ce lieu était immense et contenait aussi bien des essais que des romans. Il aimait s\'y rendre pour feuilleter'
            + ' divers ouvrages, découvrir des personnages, se passionner pour des intrigues.',
        juste: 'une bibliothèque.', faux: ['un laboratoire.', 'un musée.', 'un théâtre.'],
    },
    {
        texte: 'Ce lieu était bruyant et rempli de voyageurs pressés. Ils consultaient le tableau des horaires, attendaient'
            + ' sur le quai puis montaient dans les wagons.',
        juste: 'une gare.', faux: ['un aéroport.', 'un port.', 'un parc d\'attractions.'],
    },
    {
        texte: 'Ce lieu était calme et sentait le désinfectant. Dans les chambres, des infirmières soignaient les patients,'
            + ' et des visiteurs attendaient dans les couloirs.',
        juste: 'un hôpital.', faux: ['un hôtel.', 'une pharmacie.', 'une école.'],
    },
    {
        texte: 'Ce lieu était immense et plein de supporters. Ils chantaient dans les tribunes et applaudissaient chaque but'
            + ' marqué sur la pelouse.',
        juste: 'un stade.', faux: ['un jardin.', 'une salle de concert.', 'un cirque.'],
    },
    {
        texte: 'Ce lieu était immense et accueillait des animaux venus du monde entier. Les visiteurs observaient les lions,'
            + ' les girafes et les singes derrière de hautes grilles.',
        juste: 'un zoo.', faux: ['une ferme.', 'un cirque.', 'une prison.'],
    },
    {
        texte: 'Ce lieu était immense et très éclairé. On y prenait un panier à l\'entrée, on le remplissait dans les rayons,'
            + ' puis on faisait la queue à la caisse.',
        juste: 'un supermarché.', faux: ['une bibliothèque.', 'une banque.', 'un terrain de basket.'],
    },
    {
        texte: 'Ce lieu était humide et résonnait de cris joyeux. On y enfilait son maillot dans une cabine, puis on plongeait'
            + ' dans le grand bain.',
        juste: 'une piscine.', faux: ['une salle de bain.', 'une plage.', 'un magasin de vêtements.'],
    },
    {
        texte: 'Ce lieu était silencieux et rempli de tableaux et de statues. Les visiteurs passaient de salle en salle pour'
            + ' admirer les œuvres des grands peintres.',
        juste: 'un musée.', faux: ['une école.', 'une bibliothèque.', 'un théâtre.'],
    },
    {
        texte: 'Ce lieu était lumineux et très animé en été. On y étendait sa serviette sur le sable, on construisait des'
            + ' châteaux et on se baignait dans les vagues.',
        juste: 'une plage.', faux: ['une piscine.', 'un château.', 'une salle de bain.'],
    },
    {
        texte: 'Ce lieu sentait bon le pain chaud dès six heures du matin. Derrière le comptoir, on voyait le four, et les'
            + ' clients repartaient avec des baguettes et des croissants.',
        juste: 'une boulangerie.', faux: ['un restaurant.', 'un café.', 'un marché.'],
    },
    {
        texte: 'Ce lieu était plongé dans le noir. Installés dans de grands fauteuils, les spectateurs mangeaient du pop-corn'
            + ' en regardant l\'écran géant.',
        juste: 'un cinéma.', faux: ['un théâtre.', 'une chambre.', 'un salon.'],
    },
    {
        texte: 'Ce lieu était immense et rempli de voyageurs chargés de valises. Ils faisaient enregistrer leurs bagages,'
            + ' passaient les contrôles, puis regardaient les avions décoller derrière les vitres.',
        juste: 'un aéroport.', faux: ['une gare.', 'un hôtel.', 'une agence de voyages.'],
    },
    {
        texte: 'Ce lieu était calme pendant la leçon. Assis à leur table, les élèves recopiaient le tableau dans leur cahier,'
            + ' pendant que le professeur corrigeait des copies.',
        juste: 'une salle de classe.', faux: ['un musée.', 'une cantine.', 'une bibliothèque.'],
    },
    {
        texte: 'Ce lieu sentait l\'huile et l\'essence. Les mécaniciens réparaient les moteurs, changeaient les pneus et'
            + ' vérifiaient les freins des voitures.',
        juste: 'un garage.', faux: ['une cuisine.', 'un parking.', 'un magasin de vélos.'],
    },
    {
        texte: 'Ce lieu était entouré de champs. Chaque matin, on y trayait les vaches, on nourrissait les poules et on'
            + ' ramassait les œufs.',
        juste: 'une ferme.', faux: ['une étable.', 'un poulailler.', 'un marché.'],
    },
    {
        texte: 'Ce lieu était plongé dans le silence. Le rideau rouge se levait, et les comédiens entraient sur la scène'
            + ' devant des centaines de spectateurs.',
        juste: 'un théâtre.', faux: ['un cinéma.', 'un cirque.', 'une salle de concert.'],
    },
    {
        texte: 'Ce lieu était calme et frais. On y entendait sonner les cloches, et la lumière colorée des vitraux tombait'
            + ' sur les bancs de bois.',
        juste: 'une église.', faux: ['un château.', 'un parc.', 'une école.'],
    },
];
const genQ13 = () => {
    const { texte, juste, faux } = randomChoice(LIEUX_Q13);
    verifier(texte.startsWith('Ce lieu ') && [juste, ...faux].every((c) => /^une? /.test(c)), `Q13 : un lieu à nommer (${juste})`);
    return qcm(juste, faux, `<p>« ${texte} »</p><p>Le lieu évoqué par ces phrases est…</p>`, L13);
};

/**
 * Q14 : Identifier les mots appartenant à un champ lexical en contexte.
 * Item : « Le narrateur raconte l'arrivée d'un orage. « Je n'avais pas peur, mais je sentais une inquiétude
 *   étrange, une angoisse profonde, animale. […] C'était un murmure lointain, une rumeur trop faible pour
 *   inquiéter les échos, mais frissonnante, continue, magique. » Marcel Pagnol, Le Château de ma mère,
 *   1957. Les mots suivants évoquent une émotion. » ; tableau Vrai / Faux, les quatre mots soulignés dans
 *   le texte : inquiétude (Vrai), angoisse (Vrai), rumeur (Faux), frissonnante (Faux).
 * Domaine : un court récit à la première personne, quatre mots soulignés, dans l'ordre du texte : deux
 *   noms d'émotion, un nom de bruit ou de lumière, un adjectif de tremblement qui décrit une chose ;
 *   l'item et 15 récits écrits pour ce module (sans citation ni auteur), une variante chacun. Les lignes
 *   du tableau sont relues dans le texte (garde). Écartés : gémir, plaintif, lugubre, sinistre (ils
 *   disent eux-mêmes une émotion), timidité, curiosité (traits de caractère plus qu'émotions), éclats (de
 *   rire), des mains tremblantes (c'est le narrateur qui tremble : l'émotion y est).
 * Erreurs : rumeur (un bruit, et un mot qui fait penser à une nouvelle inquiétante), frissonnante (le
 *   frisson fait penser à la peur, mais le mot décrit ici le bruit) : des mots voisins de l'émotion, qui
 *   décrivent une sensation. Les variantes : un bruit ou une lueur (grondement, craquement, lueur…) et une
 *   chose qui tremble (vacillante, frémissantes, tremblante…).
 */
const L14 = 'Identifier les mots appartenant à un champ lexical en contexte';
const TEXTES_Q14 = [
    {
        intro: 'Le narrateur raconte l\'arrivée d\'un orage.',
        texte: 'Je n\'avais pas peur, mais je sentais une <u>inquiétude</u> étrange, une <u>angoisse</u> profonde, animale.'
            + ' […] C\'était un murmure lointain, une <u>rumeur</u> trop faible pour inquiéter les échos, mais'
            + ' <u>frissonnante</u>, continue, magique.',
        source: 'Marcel Pagnol, <i>Le Château de ma mère</i>, 1957.',
        emotions: ['inquiétude', 'angoisse'],
    },
    {
        intro: 'Le narrateur raconte sa première nuit sous la tente.',
        texte: 'Allongé dans mon sac de couchage, je ressentais une <u>crainte</u> que je n\'osais pas avouer. Au loin,'
            + ' j\'entendais le <u>grondement</u> de la rivière, et ma lampe projetait sur la toile une lumière'
            + ' <u>vacillante</u>. Puis la <u>joie</u> de l\'aventure a chassé mes idées noires.',
        emotions: ['crainte', 'joie'],
    },
    {
        intro: 'Le narrateur raconte une promenade en forêt à la tombée de la nuit.',
        texte: 'Les arbres devenaient des ombres, et une <u>inquiétude</u> grandissait en moi. Chaque <u>craquement</u> de'
            + ' branche me faisait sursauter. Les feuilles <u>frémissantes</u> semblaient chuchoter, et une véritable'
            + ' <u>panique</u> m\'a fait courir jusqu\'à la maison.',
        emotions: ['inquiétude', 'panique'],
    },
    {
        intro: 'Le narrateur raconte la fête de son anniversaire.',
        texte: 'Quand j\'ai ouvert la porte du salon, une immense <u>surprise</u> m\'a cloué sur place : tous mes amis'
            + ' étaient là ! Les bougies jetaient sur le gâteau une <u>lueur</u> douce et <u>tremblante</u>, et j\'étais'
            + ' rempli de <u>bonheur</u>.',
        emotions: ['surprise', 'bonheur'],
    },
    {
        intro: 'Le narrateur raconte son premier jour dans un nouveau collège.',
        texte: 'En franchissant le grand portail, je sentais une <u>appréhension</u> qui me rendait muet. La cour résonnait'
            + ' d\'un <u>bourdonnement</u> continu, et les feuilles <u>tremblantes</u> des platanes s\'agitaient'
            + ' au-dessus de nous. Mais quand un garçon m\'a souri, un vrai <u>soulagement</u> m\'a envahi.',
        emotions: ['appréhension', 'soulagement'],
    },
    {
        intro: 'Le narrateur raconte une sortie en bateau par gros temps.',
        texte: 'Les vagues se dressaient devant nous, et une <u>terreur</u> soudaine m\'a saisi. Le vent poussait un'
            + ' <u>sifflement</u> aigu, et la coque <u>frissonnante</u> craquait sous les coups de la mer. Quand nous'
            + ' sommes enfin rentrés au port, j\'ai ressenti une immense <u>fierté</u>.',
        emotions: ['terreur', 'fierté'],
    },
    {
        intro: 'Le narrateur raconte le soir où son chien n\'est pas rentré.',
        texte: 'Mon chien n\'était toujours pas là, et un profond <u>chagrin</u> me serrait la gorge. Dehors, la pluie'
            + ' tombait avec un <u>clapotis</u> régulier, et la flamme <u>vacillante</u> d\'une bougie éclairait la'
            + ' cuisine. Soudain, un aboiement derrière la porte : quelle <u>joie</u> !',
        emotions: ['chagrin', 'joie'],
    },
    {
        intro: 'Le narrateur raconte un repas à la cantine.',
        texte: 'Quand mon voisin a renversé mon plateau, une violente <u>colère</u> est montée en moi. Le <u>fracas</u> des'
            + ' assiettes a résonné dans toute la cantine, et les verres <u>tremblants</u> s\'entrechoquaient sur la table. Puis'
            + ' j\'ai vu son air désolé, et j\'ai eu <u>honte</u> d\'avoir crié.',
        emotions: ['colère', 'honte'],
    },
    {
        intro: 'Le narrateur raconte la visite d\'un vieux château.',
        texte: 'Dès que la lourde porte s\'est ouverte, une <u>frayeur</u> m\'a glacé le dos. Un <u>écho</u> lointain'
            + ' répétait nos pas, et une lumière <u>frémissante</u> tombait des vitraux. Pourtant, en découvrant la salle'
            + ' du trône, j\'ai ressenti un véritable <u>émerveillement</u>.',
        emotions: ['frayeur', 'émerveillement'],
    },
    {
        intro: 'Le narrateur raconte une compétition de natation.',
        texte: 'Debout sur le plot de départ, je sentais une <u>angoisse</u> terrible me nouer l\'estomac. Le'
            + ' <u>brouhaha</u> des tribunes résonnait sous le plafond, et l\'eau <u>frissonnante</u> brillait sous les'
            + ' projecteurs. Quand j\'ai touché le mur le premier, une <u>joie</u> immense m\'a submergé.',
        emotions: ['angoisse', 'joie'],
    },
    {
        intro: 'Le narrateur raconte une nuit chez sa grand-mère.',
        texte: 'Dans cette chambre inconnue, une légère <u>peur</u> m\'empêchait de fermer les yeux. Le <u>grincement</u>'
            + ' du parquet me faisait sursauter, et une ombre <u>tremblante</u> dansait sur le mur. Mais en entendant ma'
            + ' grand-mère chanter doucement, je me suis endormi, le cœur plein de <u>bonheur</u>.',
        emotions: ['peur', 'bonheur'],
    },
    {
        intro: 'Le narrateur raconte son premier spectacle de danse.',
        texte: 'Derrière le rideau, une grande <u>anxiété</u> me gagnait. Dans les coulisses, on entendait le'
            + ' <u>froissement</u> des costumes, et les rubans <u>frémissants</u> des danseuses brillaient sous les'
            + ' projecteurs. À la fin, sous les applaudissements, j\'ai ressenti une immense <u>fierté</u>.',
        emotions: ['anxiété', 'fierté'],
    },
    {
        intro: 'Le narrateur raconte une sortie au marché de Noël.',
        texte: 'En découvrant les chalets illuminés, j\'ai ressenti un véritable <u>émerveillement</u>. Le <u>tintement</u>'
            + ' des clochettes se mêlait aux rires, et les guirlandes <u>scintillantes</u> se balançaient au vent. Mais'
            + ' quand j\'ai perdu mes parents dans la foule, une vraie <u>panique</u> m\'a saisi.',
        emotions: ['émerveillement', 'panique'],
    },
    {
        intro: 'Le narrateur raconte une partie de cache-cache dans le grenier.',
        texte: 'Caché derrière une vieille malle, je retenais mon souffle, plein d\'<u>excitation</u>. Le <u>craquement</u>'
            + ' du plancher se rapprochait, et une toile d\'araignée <u>tremblante</u> pendait au-dessus de ma tête. Quand'
            + ' mon cousin m\'a enfin trouvé, j\'ai ressenti une petite <u>déception</u>.',
        emotions: ['excitation', 'déception'],
    },
    {
        intro: 'Le narrateur raconte une baignade dans un lac de montagne.',
        texte: 'En entrant dans l\'eau glacée, j\'ai poussé un cri de <u>surprise</u>. Le <u>murmure</u> du vent passait'
            + ' dans les roseaux <u>frémissants</u>, et le soleil réchauffait mes épaules. Bientôt, une douce'
            + ' <u>gaieté</u> m\'a envahi et j\'ai nagé jusqu\'au ponton.',
        emotions: ['surprise', 'gaieté'],
    },
    {
        intro: 'Le narrateur raconte une panne de courant.',
        texte: 'Soudain, toutes les lumières se sont éteintes, et une vive <u>inquiétude</u> m\'a saisi. Le'
            + ' <u>ronronnement</u> du réfrigérateur s\'était arrêté, et seule la flamme <u>tremblante</u> d\'une bougie'
            + ' éclairait le salon. Quand la lumière est revenue, j\'ai poussé un soupir de <u>soulagement</u>.',
        emotions: ['inquiétude', 'soulagement'],
    },
];
const genQ14 = () => {
    const { intro, texte, source, emotions } = randomChoice(TEXTES_Q14);
    // Les lignes du tableau : les mots soulignés, relus dans le texte, dans son ordre.
    const soulignes = texte.split('<u>').slice(1).map((morceau) => morceau.split('</u>')[0]);
    verifier(soulignes.length === 4 && emotions.every((e) => soulignes.includes(e)),
        `Q14 : quatre mots soulignés dont les émotions ${emotions.join(', ')}`);
    const bonnes = soulignes.map((mot) => (emotions.includes(mot) ? 0 : 1));
    verifier(bonnes.filter((b) => b === 0).length === 2, `Q14 : deux émotions sur quatre (${soulignes.join(', ')})`);
    return tableau(soulignes, bonnes, ['Vrai', 'Faux'],
        `<p>${intro}</p><p>« ${texte} »</p>${source ? `<p>${source}</p>` : ''}<p>Les mots suivants évoquent une émotion.</p>`,
        L14);
};

/**
 * Q15 : Choisir le mot approprié en contexte.
 * Item : « Dans son devoir, Ugo a écrit : « Le texte parle de l'enfance de l'auteur. » Pour améliorer sa
 *   phrase, Ugo pourrait remplacer « parle de » par une des expressions suivantes : laquelle ? Le texte…
 *   … l'enfance de l'auteur. » ; se souvient de, s'agit de, évoque (juste), exploite.
 * Domaine : une phrase d'élève « [un écrit, un film] parle de [la, l'…] » ; le juste est un verbe plus
 *   précis qui convient au sujet ; 16 phrases, une variante chacune. Écartés comme pièges : raconte,
 *   présente, annonce ou évoque là où ils seraient justes aussi ; s'intéresse à, se moque de, s'occupe de,
 *   souffre de (un écrit peut le faire) ; dissimule, efface, épargne (un sens possible dans la phrase).
 * Erreurs : « se souvient de » : un verbe qui demande une personne (un texte ne se souvient pas), proche
 *   du sujet (l'enfance) ; « s'agit de » : « s'agir » ne se construit qu'avec « il » (« il s'agit de »),
 *   « le texte s'agit de » est fautif ; « exploite » : un mot recherché, mais qui veut dire « tirer
 *   profit ». Les variantes : un verbe de personne proche du sujet, « s'agit de », un mot recherché au sens
 *   étranger.
 */
const L15 = 'Choisir le mot approprié en contexte';
const REDACTIONS_Q15 = [
    {
        cadre: 'Dans son devoir', prenom: 'Ugo', sujet: 'Le texte', complement: 'l\'enfance de l\'auteur',
        juste: 'évoque', faux: ['se souvient de', 's\'agit de', 'exploite'],
    },
    {
        cadre: 'Dans son exposé', prenom: 'Inès', sujet: 'Cet article', complement: 'la pollution des océans',
        juste: 'traite de', faux: ['se plaint de', 's\'agit de', 'profite de'],
    },
    {
        cadre: 'Dans son devoir', prenom: 'Malo', sujet: 'Ce livre', complement: 'la vie des pharaons',
        juste: 'décrit', faux: ['rêve de', 's\'agit de', 'abuse de'],
    },
    {
        cadre: 'Dans sa rédaction', prenom: 'Lina', sujet: 'Le poème', complement: 'la beauté de l\'automne',
        juste: 'célèbre', faux: ['admire', 's\'agit de', 'néglige'],
    },
    {
        cadre: 'Dans sa rédaction', prenom: 'Sacha', sujet: 'La chanson', complement: 'la tristesse du départ',
        juste: 'exprime', faux: ['regrette', 's\'agit de', 'économise'],
    },
    {
        cadre: 'Dans son exposé', prenom: 'Noah', sujet: 'Ce documentaire', complement: 'la vie des abeilles',
        juste: 'montre', faux: ['a peur de', 's\'agit de', 'envahit'],
    },
    {
        cadre: 'Dans son devoir', prenom: 'Jade', sujet: 'Le roman', complement: 'l\'amitié entre deux enfants',
        juste: 'raconte', faux: ['se réjouit de', 's\'agit de', 'épuise'],
    },
    {
        cadre: 'Dans son exposé', prenom: 'Yanis', sujet: 'Ce manuel', complement: 'la formation des volcans',
        juste: 'explique', faux: ['s\'inquiète de', 's\'agit de', 'provoque'],
    },
    {
        cadre: 'Dans son devoir', prenom: 'Hugo', sujet: 'Le conte', complement: 'la ruse du renard',
        juste: 'met en scène', faux: ['se méfie de', 's\'agit de', 'gaspille'],
    },
    {
        cadre: 'Dans son devoir', prenom: 'Rose', sujet: 'L\'affiche', complement: 'la fête du collège',
        juste: 'annonce', faux: ['a hâte de', 's\'agit de', 'supprime'],
    },
    {
        cadre: 'Dans son devoir', prenom: 'Éva', sujet: 'La fable', complement: 'l\'orgueil du corbeau',
        juste: 'dénonce', faux: ['regrette', 's\'agit de', 'imite'],
    },
    {
        cadre: 'Dans son exposé', prenom: 'Léon', sujet: 'Ce reportage', complement: 'la vie des pompiers',
        juste: 'présente', faux: ['se soucie de', 's\'agit de', 'remplace'],
    },
    {
        cadre: 'Dans son devoir', prenom: 'Lucas', sujet: 'La pièce de théâtre', complement: 'la jalousie de deux sœurs',
        juste: 'met en scène', faux: ['se plaint de', 's\'agit de', 'perfectionne'],
    },
    {
        cadre: 'Dans son exposé', prenom: 'Zoé', sujet: 'Le tableau', complement: 'la vie à la campagne',
        juste: 'représente', faux: ['regrette', 's\'agit de', 'cultive'],
    },
    {
        cadre: 'Dans son exposé', prenom: 'Adam', sujet: 'Ce graphique', complement: 'l\'évolution des températures',
        juste: 'montre', faux: ['s\'inquiète de', 's\'agit de', 'accélère'],
    },
    {
        cadre: 'Dans son devoir', prenom: 'Maël', sujet: 'Ce panneau', complement: 'la fermeture de la piscine',
        juste: 'signale', faux: ['se désole de', 's\'agit de', 'provoque'],
    },
];
const genQ15 = () => {
    const { cadre, prenom, sujet, complement, juste, faux } = randomChoice(REDACTIONS_Q15);
    verifier(faux.includes('s\'agit de') && /^(la |l')/.test(complement), `Q15 : « s'agit de » et un complément en « la » ou « l' » (${sujet})`);
    return qcm(juste, faux,
        `<p>${cadre}, ${prenom} a écrit : « ${sujet} parle de ${complement}. »</p>`
        + `<p>Pour améliorer sa phrase, ${prenom} pourrait remplacer « parle de » par une des expressions suivantes : laquelle ?</p>`
        + `<p>${sujet} … ${complement}.</p>`,
        L15);
};

const GENERATORS = {
    q01_qcm_unique: genQ01,
    q02_qcm_unique: genQ02,
    q03_qcm_unique: genQ03,
    q04_qcm_unique: genQ04,
    q05_qcm_unique: genQ05,
    q06_qcm_unique: genQ06,
    q07_qcm_unique: genQ07,
    q08_qcm_unique: genQ08,
    q09_qcm_unique: genQ09,
    q10_qcm_unique: genQ10,
    q11_tableau: genQ11,
    q12_qcm_unique: genQ12,
    q13_qcm_unique: genQ13,
    q14_tableau: genQ14,
    q15_qcm_unique: genQ15,
};

export const ITEMS = [
    { questionNum: 'Q01', formatKey: 'q01_qcm_unique', libelle: L01, bonneReponseOfficielle: 'malade' },
    { questionNum: 'Q02', formatKey: 'q02_qcm_unique', libelle: L02, bonneReponseOfficielle: 'peut être mangée.' },
    { questionNum: 'Q03', formatKey: 'q03_qcm_unique', libelle: L03, bonneReponseOfficielle: 'joueur' },
    { questionNum: 'Q04', formatKey: 'q04_qcm_unique', libelle: L04, bonneReponseOfficielle: 'main' },
    { questionNum: 'Q05', formatKey: 'q05_qcm_unique', libelle: L05, bonneReponseOfficielle: 'dure plusieurs années.' },
    { questionNum: 'Q06', formatKey: 'q06_qcm_unique', libelle: L06, bonneReponseOfficielle: 'permettre.' },
    { questionNum: 'Q07', formatKey: 'q07_qcm_unique', libelle: L07, bonneReponseOfficielle: 'être un peu malade.' },
    { questionNum: 'Q08', formatKey: 'q08_qcm_unique', libelle: L08, bonneReponseOfficielle: 'délicieux.' },
    { questionNum: 'Q09', formatKey: 'q09_qcm_unique', libelle: L09, bonneReponseOfficielle: 'Louise donne son bonnet… .' },
    { questionNum: 'Q10', formatKey: 'q10_qcm_unique', libelle: L10, bonneReponseOfficielle: 'jugé' },
    {
        questionNum: 'Q11', formatKey: 'q11_tableau', libelle: L11,
        bonneReponseOfficielle: '[Ils ont eu à [peine] le temps de finir.|Idée de justesse] [Elle [peine] à faire ses exercices.|Idée de difficulté]'
            + ' [Ce que tu lui as dit lui a fait beaucoup de [peine].|Idée de chagrin] [Il éprouve de la [peine] à parler.|Idée de difficulté]',
    },
    { questionNum: 'Q12', formatKey: 'q12_qcm_unique', libelle: L12, bonneReponseOfficielle: 'l’avenir.' },
    { questionNum: 'Q13', formatKey: 'q13_qcm_unique', libelle: L13, bonneReponseOfficielle: 'une bibliothèque.' },
    {
        questionNum: 'Q14', formatKey: 'q14_tableau', libelle: L14,
        bonneReponseOfficielle: '[inquiétude|Vrai] [frissonnante|Faux] [rumeur|Faux] [angoisse|Vrai]',
    },
    { questionNum: 'Q15', formatKey: 'q15_qcm_unique', libelle: L15, bonneReponseOfficielle: 'évoque' },
];

export const CATEGORIES = ITEMS.map(({ questionNum, formatKey, libelle }) => ({
    id: questionNum.toLowerCase(),
    label: `${questionNum} · ${libelle}`,
    formatsList: [{ key: formatKey, label: formatKey.endsWith('_tableau') ? 'Tableau' : 'QCM' }],
}));

export const generate = (typeExercice) => {
    const key = GENERATORS[typeExercice] ? typeExercice : randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
